/**
 * The purpose of this context is to measure the rendered width (in pixels) of the
 * primary search input element so that it can be used to render the search dropdown
 * with the same width.
 *
 * The width of the input is subject to many edge cases and is in some cases dynamic;
 * so if we want to know the width, we can't just compute it, we have to measure it.
 *
 */
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { throttle } from 'lodash'

type TNavContext = {
  searchInputWidth: number
  setShowDropdown: Dispatch<SetStateAction<boolean>>
  showDropdown: boolean
}

const NavContext = createContext<TNavContext | null>(null)

export const NavProvider = ({
  children,
  width,
}: {
  width: number
  children: React.ReactNode
}) => {
  const [showDropdown, setShowDropdown] = useState(false)

  return (
    <NavContext.Provider
      value={{ searchInputWidth: width, setShowDropdown, showDropdown }}
    >
      {children}
    </NavContext.Provider>
  )
}

export const useNav = () => {
  const ctx = useContext(NavContext)

  if (!ctx) throw new Error('NavContext unavailable')

  return ctx
}

export const useSearchInputWidth = () => {
  const [width, setWidth] = useState(300)
  const searchInputRef = useRef<HTMLDivElement>(null)

  const updateInputWidth = throttle(
    () => {
      const refNode = searchInputRef.current
      const inputElement =
        refNode?.getElementsByClassName('mc-search-input')?.[0]
      const inputWidth = inputElement?.clientWidth
      if (inputWidth && inputWidth > 200) {
        setWidth(inputWidth)
      }
    },
    200,
    { trailing: true },
  )

  useEffect(() => {
    updateInputWidth()
    window.addEventListener('resize', updateInputWidth)

    return () => {
      window.removeEventListener('resize', updateInputWidth)
    }
  }, [updateInputWidth])

  return { searchInputRef, width }
}
